import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CirclePlus, RefreshCw, Eye, SquarePen, ArrowLeftRight, Clock, Upload, CheckCircle2, Check, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { stockTransferService } from '../../services/stockTransferService';
import { StockTransferListDTO, StockTransferStatus, StockTransferStatsDTO } from '../../types';
import './StockTransferPage.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<StockTransferStatus, string> = {
  Draft:             'Draft',
  PendingApproval:   'Pending Approval',
  Approved:          'Approved',
  Rejected:          'Rejected',
  TransferredOut:    'Transferred Out',
  PartiallyReceived: 'Partially Received',
  Completed:         'Completed',
  Cancelled:         'Cancelled',
};

const STATUS_CLASS: Record<StockTransferStatus, string> = {
  Draft:             'ims-badge st-badge-draft',
  PendingApproval:   'ims-badge st-badge-pending',
  Approved:          'ims-badge st-badge-approved',
  Rejected:          'ims-badge st-badge-rejected',
  TransferredOut:    'ims-badge st-badge-transferredout',
  PartiallyReceived: 'ims-badge st-badge-partiallyreceived',
  Completed:         'ims-badge st-badge-completed',
  Cancelled:         'ims-badge st-badge-cancelled',
};

// ─── Component ────────────────────────────────────────────────────────────────

const StockTransferListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [transfers,  setTransfers]  = useState<StockTransferListDTO[]>([]);
  const [stats,      setStats]      = useState<StockTransferStatsDTO | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, statsRes] = await Promise.all([
        stockTransferService.getAll(1, 100),
        stockTransferService.getStats(),
      ]);
      const payload = listRes.data?.data;
      setTransfers(payload?.data ?? []);
      if (statsRes.data?.data) setStats(statsRes.data.data);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [listRes, statsRes] = await Promise.all([
        stockTransferService.getAll(1, 100),
        stockTransferService.getStats(),
      ]);
      const payload = listRes.data?.data;
      setTransfers(payload?.data ?? []);
      if (statsRes.data?.data) setStats(statsRes.data.data);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    { key: 'transferNumber', label: t('stockTransfer.transferNumber'), sortable: true, width: '160px', render: (v) => <code>{v as string}</code> },
    {
      key: 'transferDate',
      label: t('stockTransfer.transferDate'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    { key: 'sourceWarehouseName',      label: t('stockTransfer.source'),      sortable: true, width: '160px' },
    { key: 'destinationWarehouseName', label: t('stockTransfer.destination'),  sortable: true, width: '160px' },
    {
      key: 'status',
      label: t('common.status'),
      sortable: true,
      width: '150px',
      render: (v) => {
        const s = v as StockTransferStatus;
        return <span className={STATUS_CLASS[s] || 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    { key: 'itemCount',       label: t('stockTransfer.items'),       width: '80px' },
    { key: 'requestedByName', label: t('stockTransfer.requestedBy'), width: '140px' },
    {
      key: 'createdAt',
      label: t('common.createdAt'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('common.view'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}`),
      className: 'grid-action-btn-view',
    },
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      title: t('common.edit'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}/edit`),
      className: 'grid-action-btn-edit',
      hidden: (row) => row.status !== 'Draft',
    },
  ];

  return (
    <Layout>
      <div className="st-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('stockTransfer.title')}</h1>
          <div className="ims-list-actions">
            <button className="btn-new" onClick={() => navigate('/stock-transfers/create')}>
              <CirclePlus />
              {t('stockTransfer.newTransfer')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        {stats && (
          <div className="ims-stats-row">
            <div className="ims-stat-card">
              <div className="ims-stat-icon st-stat-icon-total">
                <ArrowLeftRight size={18} strokeWidth={2} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.totalTransfers}</span>
                <span className="ims-stat-label">{t('stockTransfer.totalTransfers')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon st-stat-icon-pending">
                <Clock size={18} strokeWidth={2} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.pendingApproval}</span>
                <span className="ims-stat-label">{t('stockTransfer.pendingApproval')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon st-stat-icon-out">
                <Upload size={18} strokeWidth={2} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.transferredOut}</span>
                <span className="ims-stat-label">{t('stockTransfer.inTransit')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon st-stat-icon-done">
                <CheckCircle2 size={18} strokeWidth={2} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.completed}</span>
                <span className="ims-stat-label">{t('stockTransfer.completed')}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Alerts ── */}
        {successMsg && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{successMsg}</span>
            <button type="button" onClick={() => setSuccessMsg(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={transfers}
            loading={loading}
            actions={actions}
            pageSize={10}
            searchPlaceholder={t('common.search', 'Search…')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={t('stockTransfer.noTransfers')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockTransferListPage;
